<template>
  <SoDialog
    :model-value="modelValue"
    :card-title="dialogTitle"
    :max-width="560"
    data-testid="publish-confirm-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div
      v-if="checking"
      class="d-flex align-center ga-3 mb-2"
      data-testid="publish-checking"
    >
      <SoProgress indeterminate :size="24" />
      <span class="text-medium-emphasis">Running template checks…</span>
    </div>
    <template v-else>
      <p class="mb-4">{{ introText }}</p>
      <SoAlert
        v-if="errorFindings.length"
        type="error"
        class="mb-4"
        data-testid="publish-errors"
      >
        <div class="font-weight-medium mb-2">
          These issues were found in the template:
        </div>
        <ul class="error-list">
          <li v-for="(finding, index) in errorFindings" :key="index">
            {{ finding.message }}
          </li>
        </ul>
      </SoAlert>
      <SoCheckbox
        v-if="errorFindings.length"
        v-model="forceAcknowledged"
        :label="acknowledgeLabel"
        data-testid="force-publish-checkbox"
      />
    </template>

    <div class="d-flex justify-end ga-2 mt-2">
      <SoButton
        title="Cancel"
        :disabled="publishing"
        data-testid="publish-confirm-cancel"
        @click="$emit('update:modelValue', false)"
      />
      <SoButton
        :title="confirmTitle"
        type="primary"
        :prepend-icon="isPublish ? 'publish' : undefined"
        :loading="publishing"
        :disabled="confirmDisabled"
        data-testid="publish-confirm-submit"
        @click="$emit('confirm')"
      />
    </div>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoProgress from "@/components/SoProgress.vue"
  import { EmailTemplateValidationService } from "@/services/admin/emailTemplateValidation/EmailTemplateValidationService"
  import { ValidationFinding } from "@/services/admin/emailTemplateValidation/emailTemplateValidation.types"

  export default defineComponent({
    components: { SoDialog, SoButton, SoAlert, SoCheckbox, SoProgress },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
      },
      htmlContent: {
        type: String,
        default: "",
      },
      plainContent: {
        type: String,
        default: "",
      },
      // What the confirm button commits to: save a draft or save & publish.
      action: {
        type: String as PropType<"draft" | "publish">,
        default: "publish",
      },
      // Save/publish mutation in flight, owned by the parent.
      publishing: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["update:modelValue", "confirm"],
    data() {
      return {
        checking: false,
        findings: [] as ValidationFinding[],
        forceAcknowledged: false,
      }
    },
    computed: {
      isPublish(): boolean {
        return this.action === "publish"
      },
      dialogTitle(): string {
        return this.isPublish ? "Publish this version?" : "Save this version?"
      },
      introText(): string {
        return this.isPublish
          ? "Publishing makes this version live and immediately used for customer communications, replacing the current live version."
          : "Saving stores this version as a draft. It will not be used for customer communications until you publish it."
      },
      confirmTitle(): string {
        return this.isPublish ? "Publish" : "Save"
      },
      acknowledgeLabel(): string {
        return this.isPublish
          ? "I understand these errors and want to publish anyway"
          : "I understand these errors and want to save anyway"
      },
      errorFindings(): ValidationFinding[] {
        return this.findings.filter((finding) => finding.severity === "error")
      },
      confirmDisabled(): boolean {
        if (this.checking) return true
        // Errors must be explicitly acknowledged before forcing through.
        return this.errorFindings.length > 0 && !this.forceAcknowledged
      },
    },
    watch: {
      modelValue: {
        immediate: true,
        handler(open: boolean) {
          if (open) this.runChecks()
        },
      },
    },
    methods: {
      async runChecks() {
        this.forceAcknowledged = false
        this.checking = true
        this.findings = []
        try {
          const content = {
            htmlContent: this.htmlContent,
            plainContent: this.plainContent,
          }
          const assets =
            await EmailTemplateValidationService.checkAssets(content)
          this.findings = [
            ...EmailTemplateValidationService.runStaticChecks(content),
            ...EmailTemplateValidationService.assetFindings(assets),
          ]
        } finally {
          this.checking = false
        }
      },
    },
  })
</script>

<style scoped>
  .error-list {
    list-style: disc;
    padding-left: 1.25rem;
    margin: 0;
  }
</style>
